import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { RefreshCw, Zap, Target, TrendingUp, AlertCircle, Clock, RotateCcw, ChevronDown } from "lucide-react";

// T1 and T2 are visually segregated:
//   T2 (full win)     -> vivid bright green + Target icon
//   T1 (partial win)  -> soft lime, lighter weight
//   SL miss           -> red
//   Expired           -> zinc grey
//   Invalidated       -> purple (was cancelled by trend flip)
const STATUS_STYLE = {
  hit_t2: { text: "text-[#22C55E]", border: "border-[#22C55E]/40", bg: "bg-[#22C55E]/5",
            label: "FULL WIN · T2", short: "T2", icon: TrendingUp },
  hit_t1: { text: "text-[#A3E635]", border: "border-[#A3E635]/40", bg: "bg-[#A3E635]/5",
            label: "PARTIAL · T1", short: "T1", icon: Target },
  miss:   { text: "text-[#EF4444]", border: "border-[#EF4444]/40", bg: "bg-[#EF4444]/5",
            label: "LOSS · SL", short: "SL", icon: AlertCircle },
  expired:{ text: "text-zinc-400", border: "border-zinc-700",     bg: "bg-zinc-800/30",
            label: "EXPIRED", short: "EX", icon: Clock },
  invalidated: { text: "text-[#A855F7]", border: "border-[#A855F7]/40", bg: "bg-[#A855F7]/5",
                 label: "INVALIDATED", short: "IV", icon: RotateCcw },
};

const FILTERS = [
  { key: "all",    label: "All" },
  { key: "hit_t2", label: "T2 only" },
  { key: "hit_t1", label: "T1 only" },
  { key: "miss",   label: "Losses" },
  { key: "expired",label: "Expired" },
  { key: "invalidated", label: "Invalidated" },
];

export default function RecentClosedSignals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/accuracy/recent?limit=50");
      setItems(data.items || []);
    } catch {} finally { setLoading(false); }
  };

  const evaluateNow = async () => {
    setEvaluating(true);
    try {
      await api.post("/admin/accuracy/evaluate-now");
      await load();
    } catch {} finally { setEvaluating(false); }
  };

  useEffect(() => { load(); }, []);

  // Counts per status for the summary strip + the closed total (excludes expired + invalidated).
  const counts = useMemo(() => {
    const c = { hit_t2: 0, hit_t1: 0, miss: 0, expired: 0, invalidated: 0 };
    for (const it of items) if (c[it.status] !== undefined) c[it.status] += 1;
    const finished = c.hit_t2 + c.hit_t1 + c.miss; // win-rate denominator
    return { ...c, finished };
  }, [items]);

  const pct = (n) => counts.finished > 0 ? ((n / counts.finished) * 100).toFixed(1) : "0.0";
  const t2Rate = pct(counts.hit_t2);
  const t1Rate = pct(counts.hit_t1);
  const slRate = pct(counts.miss);

  const visible = filter === "all" ? items : items.filter((s) => s.status === filter);

  return (
    <div className="bg-[#121212] border border-zinc-800 p-4" data-testid="admin-recent-closed">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setOpen((o) => !o)} className="text-left flex items-center gap-2 group" data-testid="recent-collapse-toggle">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#007AFF] font-mono">Admin · Backtest Audit</div>
            <div className="font-heading text-lg font-bold uppercase tracking-tight">Recent Closed Signals</div>
          </div>
          <ChevronDown className={`h-4 w-4 text-zinc-500 group-hover:text-white transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <div className="flex items-center gap-2">
          <button onClick={evaluateNow} disabled={evaluating} className="text-[10px] uppercase tracking-widest font-bold border border-zinc-700 hover:border-[#007AFF] px-2 py-1 inline-flex items-center gap-1.5" data-testid="evaluate-now-btn">
            <Zap className={`h-3 w-3 ${evaluating ? "animate-pulse" : ""}`} /> Evaluate
          </button>
          <button onClick={load} className="text-zinc-500 hover:text-white p-1" data-testid="recent-refresh">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {open && (<>
      {/* Segregated T1/T2/SL summary tiles — independent evaluation of each outcome class */}
      {counts.finished > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3" data-testid="recent-summary">
          <div className={`${STATUS_STYLE.hit_t2.bg} ${STATUS_STYLE.hit_t2.border} border p-2`} data-testid="recent-stat-t2">
            <div className={`text-[9px] uppercase tracking-widest font-mono ${STATUS_STYLE.hit_t2.text}`}>Full Win · T2</div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className={`font-heading font-black text-xl tracking-tight ${STATUS_STYLE.hit_t2.text}`}>{counts.hit_t2}</span>
              <span className="text-[10px] font-mono text-zinc-500">{t2Rate}%</span>
            </div>
          </div>
          <div className={`${STATUS_STYLE.hit_t1.bg} ${STATUS_STYLE.hit_t1.border} border p-2`} data-testid="recent-stat-t1">
            <div className={`text-[9px] uppercase tracking-widest font-mono ${STATUS_STYLE.hit_t1.text}`}>Partial · T1</div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className={`font-heading font-black text-xl tracking-tight ${STATUS_STYLE.hit_t1.text}`}>{counts.hit_t1}</span>
              <span className="text-[10px] font-mono text-zinc-500">{t1Rate}%</span>
            </div>
          </div>
          <div className={`${STATUS_STYLE.miss.bg} ${STATUS_STYLE.miss.border} border p-2`} data-testid="recent-stat-sl">
            <div className={`text-[9px] uppercase tracking-widest font-mono ${STATUS_STYLE.miss.text}`}>Loss · SL</div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className={`font-heading font-black text-xl tracking-tight ${STATUS_STYLE.miss.text}`}>{counts.miss}</span>
              <span className="text-[10px] font-mono text-zinc-500">{slRate}%</span>
            </div>
          </div>
        </div>
      )}
      {(counts.expired > 0 || counts.invalidated > 0) && (
        <div className="text-[10px] font-mono text-zinc-500 mb-3 flex gap-3" data-testid="recent-summary-aux">
          {counts.expired > 0 && <span><span className="text-zinc-300">{counts.expired}</span> expired</span>}
          {counts.invalidated > 0 && <span><span className="text-zinc-300">{counts.invalidated}</span> invalidated</span>}
          <span className="ml-auto">Closed: <span className="text-zinc-300">{counts.finished}</span></span>
        </div>
      )}

      {/* Filter pills */}
      <div className="flex flex-wrap gap-1 mb-3" data-testid="recent-filters">
        {FILTERS.map((f) => {
          const isActive = filter === f.key;
          const n = f.key === "all" ? items.length : counts[f.key] || 0;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-[10px] uppercase tracking-widest font-mono px-2 py-1 border ${isActive ? "border-[#007AFF] text-[#007AFF]" : "border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700"}`}
              data-testid={`recent-filter-${f.key}`}
            >
              {f.label} <span className="text-zinc-600">({n})</span>
            </button>
          );
        })}
      </div>

      {items.length === 0 && (
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 py-4 text-center">No closed signals yet — they appear here as trades hit SL / T1 / T2 or expire.</div>
      )}

      {items.length > 0 && visible.length === 0 && (
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 py-4 text-center">No trades match this filter.</div>
      )}

      {visible.length > 0 && (
        <div className="space-y-1 max-h-96 overflow-auto" data-testid="recent-rows">
          {visible.map((s) => {
            const st = STATUS_STYLE[s.status] || STATUS_STYLE.expired;
            const Icon = st.icon;
            return (
              <div key={s.signal_id}
                   className={`grid grid-cols-12 gap-2 items-center text-[11px] font-mono py-1.5 px-2 border-l-2 ${st.border} ${st.bg}`}
                   data-testid={`recent-row-${s.signal_id}`}>
                <div className="col-span-3 text-zinc-300 truncate">
                  <span className="text-zinc-500 mr-1">{s.mode === "swing" ? "SW" : "DT"}</span>{s.symbol}
                </div>
                <div className="col-span-2 text-zinc-500 uppercase">{s.trend}</div>
                <div className={`col-span-3 font-bold inline-flex items-center gap-1 ${st.text}`} data-testid={`recent-status-${s.signal_id}`}>
                  <Icon className="h-3 w-3" /> {st.label}
                </div>
                <div className="col-span-2 text-zinc-500 text-right">{s.initial_probability}%</div>
                <div className="col-span-2 text-zinc-600 text-right truncate" title={s.closed_at || s.generated_at}>
                  {new Date(s.closed_at || s.generated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>)}
    </div>
  );
}
